<template>
  <div class="mt-4 mx-3 row white-box px-3 py-4">
    <div class="white-box-top"></div>

    <div class="col-12 mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="mb-1">AI Workflow Manager</h2>
          <p class="text-muted mb-0">
            Define AI-powered workflows that automatically handle customer
            messages. When a new message arrives, active workflows are checked
            against the message content and executed if relevant.
          </p>
        </div>
        <button class="btn btn-primary" @click="openCreateModal">
          <BaseIcon name="plus" /> New Workflow
        </button>
      </div>
    </div>

    <div class="col-12" v-if="loading">
      <div class="text-center py-5">
        <div class="spinner-border" role="status"></div>
      </div>
    </div>

    <div class="col-12" v-else-if="workflows.length === 0">
      <div class="text-center py-5 text-muted">
        <p class="mb-2">No workflows defined yet.</p>
        <p>Create your first AI workflow to automate customer interactions.</p>
      </div>
    </div>

    <div class="col-12" v-else>
      <div class="row g-3">
        <div v-for="wf in workflows" :key="wf.id" class="col-12 col-lg-6">
          <div
            class="border rounded p-3 h-100 d-flex flex-column"
            :class="{ 'bg-light': !wf.active }"
          >
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-center gap-2">
                <span
                  class="badge"
                  :class="wf.active ? 'bg-success' : 'bg-secondary'"
                >
                  {{ wf.active ? "Active" : "Inactive" }}
                </span>
                <h5 class="mb-0">{{ wf.name }}</h5>
              </div>
              <div class="btn-group btn-group-sm">
                <button
                  class="btn btn-outline-primary"
                  @click="openEditModal(wf)"
                  title="Edit"
                >
                  <BaseIcon name="edit" />
                </button>
                <button
                  class="btn btn-outline-danger"
                  @click="confirmDelete(wf)"
                  title="Delete"
                >
                  <BaseIcon name="trash" />
                </button>
                <button
                  class="btn btn-outline-info"
                  @click="openTestPanel(wf)"
                  title="Test"
                >
                  <BaseIcon name="play-circle" />
                </button>
              </div>
            </div>

            <div class="mb-2 flex-grow-1">
              <div class="mb-2">
                <small class="text-muted fw-bold">Trigger:</small>
                <p class="mb-0 small">{{ wf.trigger }}</p>
              </div>
              <div>
                <small class="text-muted fw-bold">Action:</small>
                <p class="mb-0 small">{{ wf.action }}</p>
              </div>
              <div v-if="wf.answering_model" class="mt-1">
                <small class="text-muted fw-bold">Answering Model:</small>
                <span class="badge bg-dark text-white ms-1 small">{{
                  wf.answering_model
                }}</span>
              </div>
              <div v-if="wf.answer_style" class="mt-1">
                <small class="text-muted fw-bold">Answer Style:</small>
                <p class="mb-0 small text-muted fst-italic">
                  {{ wf.answer_style }}
                </p>
              </div>
            </div>

            <div v-if="wf.tools && wf.tools.length > 0">
              <small class="text-muted fw-bold">Allowed tools:</small>
              <div class="d-flex flex-wrap gap-1 mt-1">
                <span
                  v-for="tool in allowedToolNames(wf.tools)"
                  :key="tool"
                  class="badge bg-info text-white small"
                >
                  {{ tool }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 mt-4" v-if="activeTestWorkflow">
      <hr />
      <h4>Test: {{ activeTestWorkflow.name }}</h4>
      <div class="row mt-3">
        <div class="col-md-6 mb-3">
          <label class="form-label bold-12 text-gray">Test Message</label>
          <textarea
            class="form-control"
            rows="4"
            v-model="testMessage"
            placeholder="Enter a test customer message..."
          ></textarea>
        </div>
        <div class="col-md-4 mb-3">
          <label class="form-label bold-12 text-gray"
            >Order ID (optional)</label
          >
          <input
            type="text"
            class="form-control"
            v-model="testOrderId"
            placeholder="Order ID for context"
          />
        </div>
        <div class="col-md-2 d-flex align-items-end mb-3">
          <button
            class="btn btn-primary w-100"
            :class="{ 'loading-spin': testLoading }"
            :disabled="testLoading || !testMessage"
            @click="runTest"
          >
            Run Test
          </button>
        </div>
      </div>

      <div v-if="testResult" class="mt-3">
        <div
          class="border rounded p-3"
          :class="testResult.triggered ? 'border-success' : 'border-warning'"
        >
          <div class="d-flex align-items-center gap-2 mb-2">
            <span
              class="badge"
              :class="testResult.triggered ? 'bg-success' : 'bg-warning'"
            >
              {{ testResult.triggered ? "Triggered" : "Not Triggered" }}
            </span>
            <span class="fw-bold">{{ testResult.workflow_name }}</span>
          </div>

          <div v-if="testResult.triggered && testResult.result" class="mt-2">
            <div class="mb-2">
              <span
                class="badge"
                :class="
                  testResult.result.should_reply ? 'bg-primary' : 'bg-secondary'
                "
              >
                {{
                  testResult.result.should_reply ? "Should Reply" : "No Reply"
                }}
              </span>
              <span
                class="badge ms-1"
                :class="
                  testResult.result.order_changed ? 'bg-info' : 'bg-secondary'
                "
              >
                {{
                  testResult.result.order_changed
                    ? "Order Changed"
                    : "Order Not Changed"
                }}
              </span>
            </div>

            <div v-if="testResult.result.reply_text" class="mb-2">
              <small class="text-muted fw-bold">Reply:</small>
              <p class="mb-0 p-2 bg-light rounded">
                {{ testResult.result.reply_text }}
              </p>
            </div>

            <div
              v-if="
                testResult.result.tool_calls &&
                testResult.result.tool_calls.length > 0
              "
            >
              <small class="text-muted fw-bold">Tool Calls:</small>
              <div
                v-for="(tc, i) in testResult.result.tool_calls"
                :key="i"
                class="mb-1 p-2 bg-light rounded small"
              >
                <span class="fw-bold">{{ tc.name }}</span>
                <pre class="mb-0 mt-1" style="font-size: 11px">{{
                  JSON.stringify(tc.arguments, null, 2)
                }}</pre>
              </div>
            </div>

            <div v-if="testResult.result.changes_made">
              <small class="text-muted fw-bold">Changes:</small>
              <p class="mb-0">{{ testResult.result.changes_made }}</p>
            </div>
          </div>

          <div v-else-if="!testResult.triggered && testResult.explanation">
            <p class="mb-0">{{ testResult.explanation }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      ref="workflowModal"
      class="modal fade"
      tabindex="-1"
      aria-hidden="true"
    >
      <div
        class="
          modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable
        "
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingWorkflow ? "Edit Workflow" : "New Workflow" }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label bold-12 text-gray">Name</label>
              <input
                type="text"
                class="form-control"
                v-model="form.name"
                placeholder="e.g., Refund Request Handler"
              />
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray">Active</label>
              <div class="form-check form-switch">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="form.active"
                  id="activeSwitch"
                />
                <label class="form-check-label" for="activeSwitch">
                  {{
                    form.active
                      ? "Workflow is active and will be evaluated for new messages"
                      : "Workflow is inactive"
                  }}
                </label>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray">Trigger</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="form.trigger"
                placeholder="Describe when this workflow should trigger. e.g., 'When a customer asks about a refund or complains about a product'"
              ></textarea>
              <small class="text-muted"
                >The AI will use this description to decide if the workflow
                matches an incoming message.</small
              >
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray">Action</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="form.action"
                placeholder="Describe what this workflow should do. e.g., 'Check the order status, verify if refund is possible, reply with refund options'"
              ></textarea>
              <small class="text-muted"
                >The AI will execute this action using the allowed tools when
                the workflow triggers.</small
              >
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray"
                >Answering Model (optional)</label
              >
              <input
                type="text"
                class="form-control"
                v-model="form.answering_model"
                placeholder="e.g., llama3, mistral, gpt-4"
              />
              <small class="text-muted"
                >If set, the internal English analysis will be forwarded to this
                model to generate the final customer-facing reply. Leave empty
                to use the default model.</small
              >
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray"
                >Answer Style (optional)</label
              >
              <textarea
                class="form-control"
                rows="2"
                v-model="form.answer_style"
                placeholder="e.g., Be friendly and empathetic, use formal language, always apologize for inconvenience..."
              ></textarea>
              <small class="text-muted"
                >Instructions for how the final reply should be styled or toned.
                Only used when an Answering Model is specified.</small
              >
            </div>

            <div class="mb-3">
              <label class="form-label bold-12 text-gray"
                >Allowed Tools & Restrictions</label
              >
              <p class="text-muted small mb-2">
                Select which tools this workflow can use and set restrictions by
                order status and delivery status.
              </p>

              <div
                v-for="tool in allTools"
                :key="tool.id"
                class="border rounded p-2 mb-2"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <label
                    class="
                      cstm-check
                      d-flex
                      align-items-center
                      gap-2
                      mb-0
                      w-100
                    "
                  >
                    <span class="text-black regular-14 fw-medium">{{
                      tool.name
                    }}</span>
                    <input
                      type="checkbox"
                      :checked="isToolAllowed(tool.id)"
                      @change="toggleTool(tool.id)"
                    />
                    <span class="checkmark"></span>
                  </label>
                </div>

                <div v-if="isToolAllowed(tool.id)" class="mt-2 ms-4">
                  <div class="row g-2">
                    <div class="col-md-6">
                      <label class="small text-muted"
                        >Allowed Order Statuses</label
                      >
                      <v-select
                        :options="orderStatusOptions"
                        v-model="toolRestrictions[tool.id].order_status"
                        label="name"
                        :reduce="(s) => s.id"
                        multiple
                        placeholder="All statuses"
                        append-to-body
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="small text-muted"
                        >Allowed Delivery Statuses</label
                      >
                      <v-select
                        :options="deliveryStatusOptions"
                        v-model="toolRestrictions[tool.id].delivery_status"
                        label="name"
                        :reduce="(s) => s.id"
                        multiple
                        placeholder="All statuses"
                        append-to-body
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :class="{ 'loading-spin': saving }"
              :disabled="saving || !form.name || !form.trigger || !form.action"
              @click="saveWorkflow"
            >
              {{ editingWorkflow ? "Update" : "Create" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import { useStore } from "vuex";
import BaseIcon from "../icons/BaseIcon.vue";
const bootstrap = require("bootstrap");

const ALL_TOOLS = [
  { id: "check_order_details", name: "Check order details" },
  { id: "search_order", name: "Search order by email address or phone number" },
  {
    id: "get_tracking",
    name: "Get package tracking information by tracking code",
  },
  { id: "update_order", name: "Update order details" },
  { id: "remove_product_from_order", name: "Remove product from order" },
  { id: "add_product_to_order", name: "Add product to order" },
  { id: "change_products_in_order", name: "Change products" },
  { id: "check_product_info", name: "Check product info" },
  { id: "search_products", name: "Search products" },
  { id: "create_ticket", name: "Create a customer support ticket" },
  { id: "create_task", name: "Create a task for a customer support ticket" },
];

const ORDER_STATUSES = [
  { id: -2, name: "Preorder" },
  { id: -1, name: "Not Finished" },
  { id: 0, name: "Waiting" },
  { id: 1, name: "Under Process" },
  { id: 2, name: "Successful" },
  { id: 3, name: "Failed" },
];

const DELIVERY_STATUSES = [
  { id: 0, name: "Draft" },
  { id: 1, name: "Under Packaging" },
  { id: 2, name: "Wait for Delivery" },
  { id: 3, name: "Under Delivery" },
  { id: 4, name: "On Hold" },
  { id: 5, name: "Delivered" },
  { id: 8, name: "Wait for Pickup" },
  { id: 6, name: "Finished" },
  { id: 7, name: "Cancelled" },
];

export default {
  name: "AiWorkflows",
  components: { BaseIcon },
  props: {
    tab: String,
  },
  data() {
    return {
      store: useStore(),
      workflows: [],
      allTools: ALL_TOOLS,
      loading: false,
      saving: false,
      editingWorkflow: null,
      form: {
        name: "",
        active: true,
        trigger: "",
        action: "",
        answering_model: "",
        answer_style: "",
        tools: [],
      },
      toolRestrictions: {},
      workflowModal: null,
      activeTestWorkflow: null,
      testMessage: "",
      testOrderId: "",
      testLoading: false,
      testResult: null,
    };
  },
  computed: {
    orderStatusOptions() {
      return ORDER_STATUSES;
    },
    deliveryStatusOptions() {
      return DELIVERY_STATUSES;
    },
  },
  mounted() {
    this.loadWorkflows();
    this.workflowModal = new bootstrap.Modal(this.$refs.workflowModal, {
      backdrop: "static",
    });
    this.$refs.workflowModal.addEventListener("hidden.bs.modal", () => {
      this.editingWorkflow = null;
      this.resetForm();
    });
  },
  methods: {
    loadWorkflows() {
      this.loading = true;
      http
        .fetch("/workflows")
        .then((data) => {
          this.workflows = data.data || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openCreateModal() {
      this.editingWorkflow = null;
      this.resetForm();
      this.workflowModal.show();
    },
    openEditModal(wf) {
      this.editingWorkflow = wf;
      this.form.name = wf.name;
      this.form.active = wf.active;
      this.form.trigger = wf.trigger;
      this.form.action = wf.action;
      this.form.answering_model = wf.answering_model || "";
      this.form.answer_style = wf.answer_style || "";
      this.form.tools = JSON.parse(JSON.stringify(wf.tools || []));
      this.initToolRestrictions();
      this.workflowModal.show();
    },
    resetForm() {
      this.form = {
        name: "",
        active: true,
        trigger: "",
        action: "",
        answering_model: "",
        answer_style: "",
        tools: [],
      };
      this.toolRestrictions = {};
    },
    initToolRestrictions() {
      this.toolRestrictions = {};
      for (const tool of ALL_TOOLS) {
        const existing = (this.form.tools || []).find(
          (t) => (t.id || t.tool_id) === tool.id
        );
        if (existing) {
          this.toolRestrictions[tool.id] = {
            order_status: existing.restrictions?.order_status || [],
            delivery_status: existing.restrictions?.delivery_status || [],
          };
        } else {
          this.toolRestrictions[tool.id] = {
            order_status: [],
            delivery_status: [],
          };
        }
      }
    },
    isToolAllowed(toolId) {
      return (this.form.tools || []).some(
        (t) => (t.id || t.tool_id) === toolId && t.allowed
      );
    },
    toggleTool(toolId) {
      const idx = (this.form.tools || []).findIndex(
        (t) => (t.id || t.tool_id) === toolId
      );
      if (idx > -1) {
        this.form.tools.splice(idx, 1);
      } else {
        if (!this.form.tools) this.form.tools = [];
        this.form.tools.push({
          id: toolId,
          tool_id: toolId,
          allowed: true,
          restrictions: {
            order_status: [],
            delivery_status: [],
          },
        });
        if (!this.toolRestrictions[toolId]) {
          this.toolRestrictions[toolId] = {
            order_status: [],
            delivery_status: [],
          };
        }
      }
    },
    buildToolsPayload() {
      return ALL_TOOLS.map((tool) => {
        const allowed = this.isToolAllowed(tool.id);
        const restrictions = this.toolRestrictions[tool.id] || {
          order_status: [],
          delivery_status: [],
        };
        return {
          id: tool.id,
          tool_id: tool.id,
          allowed,
          restrictions: {
            order_status: restrictions.order_status || [],
            delivery_status: restrictions.delivery_status || [],
          },
        };
      });
    },
    saveWorkflow() {
      this.saving = true;
      const payload = {
        name: this.form.name,
        active: this.form.active,
        trigger: this.form.trigger,
        action: this.form.action,
        answering_model: this.form.answering_model || null,
        answer_style: this.form.answer_style || null,
        tools: this.buildToolsPayload(),
      };

      const request = this.editingWorkflow
        ? http.fetch(
            "/workflows/" + this.editingWorkflow.id,
            payload,
            true,
            "PUT"
          )
        : http.fetch("/workflows", payload, true);

      request
        .then(() => {
          this.workflowModal.hide();
          this.saving = false;
          this.loadWorkflows();
        })
        .catch(() => {
          this.saving = false;
        });
    },
    confirmDelete(wf) {
      if (
        confirm('Delete workflow "' + wf.name + '"? This cannot be undone.')
      ) {
        http
          .fetch("/workflows/" + wf.id, {}, true, "DELETE")
          .then(() => {
            this.loadWorkflows();
          })
          .catch(() => {});
      }
    },
    allowedToolNames(tools) {
      if (!tools) return [];
      return tools
        .filter((t) => t.allowed)
        .map((t) => {
          const found = ALL_TOOLS.find((at) => at.id === (t.id || t.tool_id));
          return found ? found.name : t.id || t.tool_id;
        });
    },
    openTestPanel(wf) {
      this.activeTestWorkflow = wf;
      this.testMessage = "";
      this.testOrderId = "";
      this.testResult = null;
    },
    runTest() {
      this.testLoading = true;
      this.testResult = null;
      const payload = {
        message: this.testMessage,
      };
      if (this.testOrderId) {
        payload.order_id = this.testOrderId;
      }
      http
        .fetch("/workflows/" + this.activeTestWorkflow.id + "/test", payload)
        .then((data) => {
          this.testResult = data;
          this.testLoading = false;
        })
        .catch(() => {
          this.testLoading = false;
        });
    },
  },
};
</script>
